import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Map,
  BarChart3,
  Info,
  ArrowRight,
  MousePointerClick
} from 'lucide-react';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';

const counties = Array.from(new Set(Object.values(routingKeyToCounty))).sort();

const countyToRoutingKeys: Record<string, string[]> = {};
Object.entries(routingKeyToCounty).forEach(([key, county]) => {
  if (!countyToRoutingKeys[county]) countyToRoutingKeys[county] = [];
  countyToRoutingKeys[county].push(key);
});

const PriceTrendsDiscoverPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCounties = useMemo(
    () =>
      counties.filter((county) =>
        county.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm]
  );

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible bg-[#f8fafc]">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Irish Property Price Trends 2025 | Market Data by County</title>
        <meta
          name="description"
          content="Explore real-time property price trends across Ireland. Detailed market analysis, recent sales prices, and volume data for all 26 counties including Dublin, Cork, and Galway."
        />
        <meta
          name="keywords"
          content="Ireland property prices, house price trends Ireland, property market data, Irish real estate stats, house prices by county"
        />
        <link rel="canonical" href="https://rentsmart.ie/prices/discover" />

        {/* Open Graph / Social Media */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Irish Property Price Trends | Market Data by County" />
        <meta
          property="og:description"
          content="Compare property market trends across Ireland. View recent sales and price changes in your area."
        />
        <meta property="og:image" content="https://rentsmart.ie/og-price-trends.jpg" />

        {/* Structured Data for Google (SEO Power Move) */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Dataset',
            name: 'Irish Property Price Trends by County',
            description:
              'A comprehensive database of property price trends and market data for every county in Ireland.',
            license: 'https://rentsmart.ie/terms-of-service',
            creator: {
              '@type': 'Organization',
              name: 'RentSmart.ie',
            },
            variableMeasured: 'Property Sales Prices',
            spatialCoverage: 'Ireland',
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <header className="bg-blue-600 pt-16 pb-24 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/30 text-blue-100 text-xs font-bold uppercase tracking-widest mb-6">
            <BarChart3 className="h-3 w-3" /> Updated Weekly
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
            Property Price Trends <br className="hidden md:block" /> Across Ireland
          </h1>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Select a county to explore interactive price charts, average sales values, and
            historical market performance data.
          </p>

          {/* Integrated Search */}
          <div className="max-w-lg mx-auto relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by county (e.g. Dublin, Kildare...)"
              className="w-full pl-12 pr-6 py-4 rounded-xl shadow-2xl focus:ring-4 focus:ring-white/20 outline-none border-none text-gray-900 text-lg transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      {/* Grid Content */}
      <main className="max-w-6xl mx-auto px-4 -mt-12 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredCounties.map((county) => (
            <button
              key={county}
              onClick={() => navigate(`/prices/${countyToRoutingKeys[county][0]}`)}
              className="flex flex-col text-left bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-400 hover:-translate-y-1.5 transition-all group"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="p-2.5 bg-blue-50 rounded-lg group-hover:bg-blue-600 transition-colors">
                  <Map className="h-5 w-5 text-blue-600 group-hover:text-white" />
                </div>
                <ArrowRight className="h-5 w-5 text-gray-300 opacity-0 group-hover:opacity-100 group-hover:text-blue-500 transition-all -translate-x-2 group-hover:translate-x-0" />
              </div>

              <h2 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-1">
                {county}
              </h2>

              {/* Subtle Routing Keys */}
              <p className="text-[11px] text-gray-400 font-medium tracking-tight uppercase">
                Areas:{' '}
                <span className="text-gray-300 font-normal">
                  {countyToRoutingKeys[county].slice(0, 5).join(' · ')}
                  {countyToRoutingKeys[county].length > 5 && ' · +More'}
                </span>
              </p>

              <div className="mt-6 flex items-center gap-2 text-xs font-bold text-blue-600 uppercase tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
                Analyze Market <MousePointerClick className="h-3 w-3" />
              </div>
            </button>
          ))}
        </div>

        {/* Empty State */}
        {filteredCounties.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200">
            <p className="text-gray-400 text-lg">No results match your search.</p>
          </div>
        )}
      </main>

      {/* SEO Content Block (Hidden but readable by crawlers, or stylized for users) */}
      <section className="max-w-4xl mx-auto px-4 pb-20 border-t border-gray-200 pt-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="flex gap-4">
            <div className="shrink-0">
              <Info className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Detailed Sales Data</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Our discovery tool provides access to thousands of data points from the Property
                Price Register, visualized to show seasonal trends and regional growth patterns.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="shrink-0">
              <BarChart3 className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Market Indicators</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Analyze how different Eircode routing keys perform within a county to find the best
                value areas for buying or renting in the current Irish market.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PriceTrendsDiscoverPage;
